/**
 * HubActivityFeed — 학습(/students-hub) 전용 "활동 피드"(테스트 + 시험지 배정 + 리포트 시계열 통합)
 *
 * 이원화 원칙: 공유 컴포넌트 무수정. 테스트 카드(로컬 fork HubTestHistoryItem)·시험지 배정 카드
 * (HubAssignmentCard)·리포트 카드(HubReportCard)를 시각 내림차순으로 하나의 스크롤 피드에 섞어
 * 렌더한다. 채팅 말풍선처럼 테스트와 배정 = 좌측 말풍선, 리포트 = 우측 말풍선.
 *
 * 세 번째 갈래(배정)가 있는 이유는 **v1 테스트 목록이 신규 시험지를 못 담기 때문**이다 —
 * 근거는 HubAssignmentCard 머리주석에 있다. 상위 HubTestReportTabs 가
 * 카드 chrome·헤더(학습기록 타이틀 + 테스트/리포트 생성 버튼)·데이터·모달을 소유하고,
 * 여기서는 스크롤 리스트만 렌더한다.
 *
 * 동작(무한스크롤·선택 하이라이트·선택행 자동 스크롤)은 테스트 카드 기준으로 유지.
 */

import { useCallback, useEffect, useRef, useMemo } from 'react';
import HubTestHistoryItem from './HubTestHistoryItem';
import HubReportCard from './HubReportCard';
import HubAssignmentCard from './HubAssignmentCard';
// v1 테스트와 v2 배정이 한 자리(selectedTest)를 나눠 쓴다 — 두 번호 공간을 가르는 키
import { testKey } from './assignmentResult';
import { parseV2Timestamp } from '../../utils/v2DateTime';

/**
 * 목록 하나가 실패했다는 고지 + 다시 시도. 갈래가 둘(리포트·배정)이라 마크업을 한 벌로 둔다.
 * **무엇이 실패했는지 문구가 말해야 한다** — 둘 다 「목록을 불러오지 못했습니다」면
 * 피드에 빠진 것이 리포트인지 시험지인지 화면에서 구분할 수 없다.
 */
const FeedErrorBanner = ({ text, onRetry }) => (
  <div className="mb-2 flex items-center justify-between gap-2 rounded-xl border border-danger/30 bg-danger/5 px-3 py-2">
    <p className="text-xs text-danger">{text}</p>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="shrink-0 rounded-full border border-danger/40 px-2 py-0.5 text-[11px] font-medium text-danger transition-colors hover:bg-danger/10"
      >
        다시 시도
      </button>
    )}
  </div>
);

const tsOf = (v) => {
  return parseV2Timestamp(v)?.getTime() ?? 0;
};

const HubActivityFeed = ({
  testHistory,
  reports,
  assignments,
  selectedTest,
  isTestHistoryLoading,
  reportsLoading,
  reportsError,
  assignmentsLoading,
  assignmentsError,
  onReloadAssignments,
  hasMoreTests,
  onTestSelect,
  onOpenReport,
  onDeleteReport,
  onReloadReports,
  shareMap,
  deletingReportIds,
  testHistoryRef,
  onLoadMore,
  compact = false,
  recentAssignmentId = null,
  returnTo = null,
  /** 배정 카드의 [>]를 누르면 부른다 — 상위가 채점 창을 띄운다(페이지 이동 없음) */
  onOpenAssignment = null,
  /** 배정 카드 본체를 누르면 부른다 — 상위가 그 시험지를 골라 문제 미리보기를 좁힌다 */
  onSelectAssignment = null,
}) => {
  const testItemRefs = useRef({});
  const prevSelectedTestId = useRef(null);
  const recentAssignmentRef = useRef(null);
  const movedRecentAssignmentRef = useRef(null);

  // 테스트 + 리포트를 created_at 내림차순으로 병합
  const feedItems = useMemo(() => {
    const tests = (testHistory || []).map((t) => ({
      kind: 'test',
      ts: tsOf(t.created_at || t.exam_created_at),
      key: `t-${t.stamp_id}-${t.exam_id}`,
      data: t,
    }));
    const reps = (reports || []).map((r) => ({
      kind: 'report',
      ts: tsOf(r.created_at),
      key: `r-${r.report_id}`,
      data: r,
    }));
    // 배정의 시각은 assigned_at 이다(계약 §1-1 — 'Z' 가 붙은 UTC). 없으면 created_at 으로 물러선다:
    // 시각을 못 읽으면 ts=0 이 되어 **피드 맨 아래로 가라앉아** 방금 출제한 것이 안 보인다.
    const asgs = (assignments || []).map((a) => ({
      kind: 'assignment',
      ts: tsOf(a.assigned_at || a.created_at),
      key: `a-${a.assignment_id}`,
      data: a,
    }));
    return [...tests, ...reps, ...asgs].sort((a, b) => b.ts - a.ts);
  }, [testHistory, reports, assignments]);

  // 지금 골라진 것 — v1 테스트든 v2 배정이든 이 키 하나로 판정한다
  const selectedKey = testKey(selectedTest);

  const recentAssignmentKey = recentAssignmentId == null ? null : String(recentAssignmentId);
  const recentAssignmentPresent = recentAssignmentKey != null
    && (assignments || []).some((a) => String(a?.assignment_id) === recentAssignmentKey);

  /**
   * 출제 직후 목록 재조회보다 assignment id 가 먼저 온다. 그 순간에는 ref 가 없으므로 완료로
   * 표시하지 않고, 해당 행이 실제 DOM 에 붙은 렌더에서 초점과 스크롤을 한 번만 옮긴다.
   * focus 가 먼저다 — scrollIntoView 가 먼저 움직이면 브라우저의 후속 초점 스크롤이 다시 덮는다.
   */
  useEffect(() => {
    if (recentAssignmentKey == null) {
      movedRecentAssignmentRef.current = null;
      return;
    }
    if (!recentAssignmentPresent || movedRecentAssignmentRef.current === recentAssignmentKey) return;
    const el = recentAssignmentRef.current;
    if (!el) return;
    movedRecentAssignmentRef.current = recentAssignmentKey;
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [recentAssignmentKey, recentAssignmentPresent]);

  // 테스트 목록 변경 시 불필요한 ref 정리
  useEffect(() => {
    const currentExamIds = new Set((testHistory || []).map((test) => test.exam_id));
    Object.keys(testItemRefs.current).forEach((examId) => {
      if (!currentExamIds.has(parseInt(examId))) {
        delete testItemRefs.current[examId];
      }
    });
  }, [testHistory]);

  // 선택된 테스트가 변경될 때 해당 테스트로 자동 스크롤 (가운데 정렬)
  useEffect(() => {
    if (selectedTest && testHistoryRef?.current) {
      const currentTestId = selectedTest.exam_id;
      const isTestActuallyChanged = prevSelectedTestId.current !== currentTestId;

      if (isTestActuallyChanged) {
        const scrollToSelectedTest = (retryCount = 0) => {
          const selectedTestRef = testItemRefs.current[currentTestId];
          const maxRetries = 5;
          if (selectedTestRef) {
            selectedTestRef.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          } else if (retryCount < maxRetries) {
            const delay = Math.min(50 * Math.pow(2, retryCount), 800);
            setTimeout(() => scrollToSelectedTest(retryCount + 1), delay);
          }
        };
        setTimeout(() => scrollToSelectedTest(0), 50);
        prevSelectedTestId.current = currentTestId;
      }
    } else if (selectedTest) {
      prevSelectedTestId.current = selectedTest.exam_id;
    } else {
      prevSelectedTestId.current = null; // 선택 해제 시 리셋 → 같은 테스트를 다시 선택해도 자동 스크롤이 동작
    }
  }, [selectedTest?.exam_id, feedItems.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // 인피니티 스크롤 (테스트 페이지네이션)
  const handleScroll = useCallback((e) => {
    if (!onLoadMore || isTestHistoryLoading || !hasMoreTests) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      onLoadMore();
    }
  }, [onLoadMore, isTestHistoryLoading, hasMoreTests]);

  return (
    <div
      ref={testHistoryRef}
      className={`flex-1 overflow-y-auto bg-gray-50 shadow-lg ${compact ? 'space-y-1.5 p-2' : 'space-y-2 p-3'}`}
      onScroll={handleScroll}
    >
      {/* 목록 로드 실패는 테스트 카드 유무와 무관하게 항상 고지(+재시도). 테스트가 있으면 아래 피드가, 없으면 배너만 남는다. */}
      {reportsError && <FeedErrorBanner text={reportsError} onRetry={onReloadReports} />}
      {assignmentsError && <FeedErrorBanner text={assignmentsError} onRetry={onReloadAssignments} />}
      {feedItems.length > 0 ? (
        <>
          {feedItems.map((item) => {
            if (item.kind === 'test') {
              const test = item.data;
              // 키로 견준다 — 배정이 골라져 있을 때 exam_id 끼리만 견주면 값이 비어 남의 카드가 켜진다
              const isSelected = selectedKey != null && selectedKey === testKey(test);
              // 테스트 = 좌측 말풍선. 선택 카드는 원본 그대로(풀 grid), 비선택은 통계 1라인(collapsed).
              return (
                <div key={item.key} className="max-w-[90%]">
                  <HubTestHistoryItem
                    ref={(el) => {
                      if (el) testItemRefs.current[test.exam_id] = el;
                    }}
                    test={test}
                    isSelected={isSelected}
                    collapsed={!isSelected}
                    onClick={() => onTestSelect(test)}
                    compact={compact}
                  />
                </div>
              );
            }
            if (item.kind === 'assignment') {
              // 시험지 배정 = 좌측 말풍선(테스트와 같은 편). 학생이 받은 것이라는 점에서 테스트와 한 계열이다.
              const isRecent = recentAssignmentKey != null
                && String(item.data?.assignment_id) === recentAssignmentKey;
              return (
                <div key={item.key} className="max-w-[90%]">
                  <HubAssignmentCard
                    ref={isRecent ? recentAssignmentRef : null}
                    assignment={item.data}
                    compact={compact}
                    recent={isRecent}
                    returnTo={returnTo}
                    onOpen={onOpenAssignment}
                    onSelect={onSelectAssignment}
                    selected={selectedKey != null && selectedKey === testKey({ kind: 'assignment', assignment_id: item.data?.assignment_id })}
                  />
                </div>
              );
            }
            // 리포트 = 우측 말풍선
            return (
              <div key={item.key} className="ml-auto max-w-[90%]">
                <HubReportCard
                  report={item.data}
                  share={(shareMap && shareMap.get(item.data.report_id)) || null}
                  onOpenReport={onOpenReport}
                  onDeleteReport={onDeleteReport}
                  deleting={!!deletingReportIds && deletingReportIds.has(item.data.report_id)}
                />
              </div>
            );
          })}

          {(isTestHistoryLoading || reportsLoading || assignmentsLoading) && (
            <div className="flex items-center justify-center py-4">
              <p className="text-xs text-gray-500">목록을 불러오는 중...</p>
            </div>
          )}

          {/* 어느 하나라도 실패한 상태에서는 '모든 목록을 불러왔습니다'를 숨긴다(전부 다 왔다는 오인 방지). */}
          {!hasMoreTests && !isTestHistoryLoading && !reportsLoading && !assignmentsLoading
            && !reportsError && !assignmentsError && (
            <div className="flex items-center justify-center py-4">
              <p className="text-xs text-gray-500">모든 목록을 불러왔습니다.</p>
            </div>
          )}
        </>
      ) : (isTestHistoryLoading || reportsLoading || assignmentsLoading) ? (
        <div className="flex items-center justify-center py-8">
          <p className="text-sm text-gray-500">목록을 불러오는 중...</p>
        </div>
      ) : !(reportsError || assignmentsError) ? (
        // 실패 배너가 위에 이미 떠 있으면 중복 문구를 내지 않는다.
        <div className="flex items-center justify-center py-8">
          <p className="text-sm text-gray-500">표시할 테스트·시험지·리포트가 없습니다.</p>
        </div>
      ) : null}
    </div>
  );
};

export default HubActivityFeed;
